import React, { useRef } from "react";
import { motion } from "framer-motion";
import { renderPh, scrollToId, Ph } from "./fx";
import { ParticleHero } from "./ParticleHero";
import { PH } from "./copy";
import { REPLAY_VIDEO_URL, RCOPY } from "./replayCopy";
import { RManifesto, RMethod, RStack, RGuarantee, RHost, RObjections, RFaq, RBooking } from "./ReplaySections";

const ReplayNav = ({ rt }) => (
  <motion.nav
    initial={{ opacity: 0, y: -16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5, duration: 0.7 }}
    className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 md:gap-2 px-2 py-2 rounded-full glass-card"
    data-testid="replay-nav"
  >
    <button data-testid="replay-nav-watch" onClick={() => scrollToId("watch")} className="mono text-[10px] tracking-[0.2em] text-white/60 hover:text-[#4D9FFF] transition-colors px-2 md:px-3 py-2">{rt.nav.watch}</button>
    <button data-testid="replay-nav-method" onClick={() => scrollToId("method")} className="mono text-[10px] tracking-[0.2em] text-white/60 hover:text-[#4D9FFF] transition-colors px-2 md:px-3 py-2">{rt.nav.method}</button>
    <button data-testid="replay-nav-host" onClick={() => scrollToId("host")} className="mono text-[10px] tracking-[0.2em] text-white/60 hover:text-[#4D9FFF] transition-colors px-2 md:px-3 py-2">{rt.nav.host}</button>
    <button data-testid="replay-nav-faq" onClick={() => scrollToId("faq")} className="mono text-[10px] tracking-[0.2em] text-white/60 hover:text-[#4D9FFF] transition-colors px-2 md:px-3 py-2">{rt.nav.faq}</button>
    <button data-testid="replay-nav-book-cta" onClick={() => scrollToId("book")} className="btn-accent mono text-[10px] tracking-[0.2em] px-4 py-2 rounded-full">{rt.nav.book}</button>
  </motion.nav>
);

const ReplayPlayer = ({ rt, watchPctRef }) => {
  const onTime = (e) => {
    const v = e.target;
    if (!v.duration) return;
    const pct = Math.floor((v.currentTime / v.duration) * 100);
    for (const m of [100, 75, 50, 25]) {
      if (pct >= m) {
        if (watchPctRef.current < m) watchPctRef.current = m;
        break;
      }
    }
  };
  const isMp4 = REPLAY_VIDEO_URL && /\.(mp4|webm|mov)(\?|$)/i.test(REPLAY_VIDEO_URL);
  return (
    <div className="relative w-full aspect-video bg-[#123059] border border-white/12 overflow-hidden" data-testid="replay-video-player">
      <span className="corner-mark tl" /><span className="corner-mark tr" />
      <span className="corner-mark bl" /><span className="corner-mark br" />
      {REPLAY_VIDEO_URL ? (
        isMp4 ? (
          <video src={REPLAY_VIDEO_URL} controls className="absolute inset-0 w-full h-full" onTimeUpdate={onTime} />
        ) : (
          <iframe
            src={REPLAY_VIDEO_URL}
            title="Session replay"
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        )
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-8 text-center" data-testid="replay-video-pending">
          <div className="w-20 h-20 rounded-full border-2 border-[#4D9FFF] flex items-center justify-center shimmer">
            <div className="w-0 h-0 border-y-[14px] border-y-transparent border-l-[22px] border-l-[#4D9FFF] ml-1.5" />
          </div>
          <Ph>{rt.videoPending}</Ph>
          <div className="mono text-[9px] tracking-[0.35em] text-white/25">{rt.videoPendingSub}</div>
        </div>
      )}
    </div>
  );
};

const ReplayHero = ({ rt, lang, watchPctRef }) => {
  const heroRef = useRef(null);
  return (
    <section ref={heroRef} id="watch" className="relative min-h-screen grid-bg flex items-center pt-28 pb-16 md:pt-24 overflow-hidden" data-testid="replay-hero-section">
      <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-12 grid md:grid-cols-[45fr_55fr] gap-12 items-center">
        <div className="relative z-10">
          <div className="mono accent text-[10px] tracking-[0.25em] mb-6" data-testid="replay-eyebrow">{rt.eyebrow}</div>
          <h1 className="h1-display text-[clamp(34px,3.8vw,60px)]" data-testid="replay-h1">
            {rt.h1.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden">
                <motion.span
                  className={`block ${i === 1 ? "accent" : ""}`}
                  style={i === 2 ? { opacity: 0.6, fontSize: "62%" } : {}}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <p className="mt-6 text-[15px] text-white/55 max-w-lg leading-relaxed" data-testid="replay-subheadline">{rt.sub}</p>
          <blockquote className="mt-6 border-l-2 border-[#4D9FFF] pl-5 italic text-sm text-white/60 max-w-md leading-relaxed">{rt.quote}</blockquote>
          <div className="mono accent text-[11px] tracking-[0.2em] mt-6" data-testid="replay-availability">{rt.availability}</div>
          <div className="mt-8 flex items-center gap-4" data-testid="replay-host-module">
            <img src="/img/plinio.webp" alt="Plinio Angulo, Loan Officer, NMLS #1443167" loading="lazy" className="w-12 h-12 rounded-full border-2 border-[#4D9FFF] object-cover object-top shrink-0" />
            <div className="text-xs leading-relaxed">
              <div className="font-bold">Plinio Angulo</div>
              <div className="text-white/40">{PH.company}</div>
              <div className="text-white/40 mono text-[10px]">NMLS# {PH.nmls}</div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute inset-[-25%] pointer-events-none">
            <ParticleHero heroRef={heroRef} />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10"
          >
            <ReplayPlayer rt={rt} watchPctRef={watchPctRef} />
            <button
              data-testid="replay-hero-book-cta"
              data-cta
              onClick={() => scrollToId("book")}
              className="btn-accent w-full py-4 mt-4 text-sm tracking-widest uppercase"
            >
              {rt.cta}
            </button>
            <p className="text-[10px] text-white/25 text-center mt-3">{rt.trust}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ReplayTicker = ({ rt }) => (
  <section className="border-y border-white/10 py-4 overflow-hidden" data-testid="replay-ticker">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center">
          {rt.ticker.map((item, i) => (
            <span key={i} className="mono text-[11px] tracking-[0.2em] text-white/40 flex items-center whitespace-nowrap">
              <span className="px-6">{item}</span>
              <span className="accent">◆</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

export default function ReplayPage({ t, lang }) {
  const rt = RCOPY[lang];
  const watchPctRef = useRef(0);
  return (
    <div data-testid="replay-page">
      <ReplayNav rt={rt} />
      <ReplayHero rt={rt} lang={lang} watchPctRef={watchPctRef} />
      <ReplayTicker rt={rt} />
      <RManifesto rt={rt} lang={lang} />
      <RMethod rt={rt} />
      <RStack rt={rt} />
      <RGuarantee rt={rt} />
      <RHost rt={rt} t={t} />
      <RObjections rt={rt} />
      <RFaq rt={rt} />
      <RBooking rt={rt} lang={lang} watchPctRef={watchPctRef} />
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden glass-card p-3 border-t border-white/10" data-testid="replay-mobile-cta">
        <button data-cta onClick={() => scrollToId("book")} className="btn-accent w-full py-4 text-xs tracking-widest uppercase">
          {rt.cta}
        </button>
      </div>
    </div>
  );
}
